/* ============================================================
   Natural Roots — Product Tabs (NR)

   Deliberately small. Scrolling is native (overflow + scroll-snap), so the
   browser owns touch, wheel and keyboard, and every <a> is a plain link.
   This file only does three things:
     1. switch tabs
     2. drive the arrows and progress bar from the real scroll position
     3. quick-add to cart and open the drawer

   Nothing here calls setPointerCapture, preventDefault on clicks, or
   stopPropagation — the three things that break links in carousels.
   ============================================================ */
(function () {
  'use strict';

  var ROOT = '[data-nr-ptabs]';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ---------- Cart drawer ---------- */
  function openDrawer(attempt) {
    attempt = attempt || 0;
    var drawer = document.querySelector('m-cart-drawer, cart-drawer, [data-cart-drawer]');

    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    if (attempt < 10) {
      setTimeout(function () { openDrawer(attempt + 1); }, 150);
    }
  }

  function addToCart(button) {
    var variantId = button.getAttribute('data-nr-ptabs-add');
    if (!variantId || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    button.classList.remove('is-added', 'has-error');

    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
    );

    var config;
    if (typeof fetchConfig === 'function') {
      config = fetchConfig('javascript');
    } else {
      config = { headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
    }
    config.method = 'POST';
    config.body = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(root() + 'cart/add.js', config)
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status) throw new Error(res.description || res.message || 'Error');

        var theme = window.MinimogTheme || {};
        if (window.MinimogEvents && theme.pubSubEvents) {
          window.MinimogEvents.emit(theme.pubSubEvents.cartUpdate, { cart: res });
        }
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: res } }));

        button.classList.add('is-added');
        openDrawer();
      })
      .catch(function (err) {
        console.error('nr-product-tabs: add to cart failed', err);
        button.classList.add('has-error');
      })
      .finally(function () {
        setTimeout(function () {
          button.removeAttribute('aria-busy');
          button.classList.remove('is-added', 'has-error');
        }, 1200);
      });
  }

  /* ---------- Carousel (per panel) ---------- */
  function Carousel(panel) {
    this.panel = panel;
    this.scroller = panel.querySelector('[data-nr-ptabs-scroller]');
    this.prev = panel.querySelector('[data-nr-ptabs-prev]');
    this.next = panel.querySelector('[data-nr-ptabs-next]');
    this.thumb = panel.querySelector('[data-nr-ptabs-thumb]');
    if (!this.scroller) return;

    this.isRtl = getComputedStyle(this.scroller).direction === 'rtl';
    this.ticking = false;

    var self = this;
    if (this.prev) this.prev.addEventListener('click', function () { self.step(-1); });
    if (this.next) this.next.addEventListener('click', function () { self.step(1); });
    this.scroller.addEventListener('scroll', function () { self.queueUpdate(); }, { passive: true });
    window.addEventListener('resize', function () { self.queueUpdate(); }, { passive: true });

    this.update();
  }

  Carousel.prototype.stepSize = function () {
    var card = this.scroller.querySelector('[data-nr-ptabs-card]');
    if (!card) return this.scroller.clientWidth * 0.8;
    var gap = parseFloat(getComputedStyle(this.scroller).columnGap || getComputedStyle(this.scroller).gap) || 0;
    return card.getBoundingClientRect().width + gap;
  };

  // In RTL the scroll axis runs the other way, so "forward" is a negative delta
  Carousel.prototype.step = function (direction) {
    var delta = this.stepSize() * direction * (this.isRtl ? -1 : 1);
    this.scroller.scrollBy({ left: delta, behavior: 'smooth' });
  };

  Carousel.prototype.queueUpdate = function () {
    if (this.ticking) return;
    this.ticking = true;
    var self = this;
    requestAnimationFrame(function () {
      self.ticking = false;
      self.update();
    });
  };

  Carousel.prototype.update = function () {
    var s = this.scroller;
    var max = s.scrollWidth - s.clientWidth;
    var pos = Math.abs(s.scrollLeft); // abs() normalises RTL's negative values
    var atStart = pos <= 2;
    var atEnd = pos >= max - 2;

    if (this.prev) this.prev.disabled = max <= 0 || atStart;
    if (this.next) this.next.disabled = max <= 0 || atEnd;

    if (this.thumb) {
      if (max <= 0) {
        this.thumb.style.inlineSize = '100%';
        this.thumb.style.transform = 'none';
        return;
      }
      var ratio = Math.max(s.clientWidth / s.scrollWidth, 0.08);
      var width = ratio * 100;
      var travel = (100 - width) / width; // in multiples of thumb width
      var progress = pos / max;
      this.thumb.style.inlineSize = width + '%';
      // translateX is physical; flip for RTL so the thumb moves the right way
      var sign = this.isRtl ? -1 : 1;
      this.thumb.style.transform = 'translateX(' + (sign * travel * progress * 100) + '%)';
    }
  };

  /* ---------- Tabs (per section) ---------- */
  function initSection(section) {
    if (section.dataset.nrPtabsReady === 'true') return;
    section.dataset.nrPtabsReady = 'true';

    var tabs = Array.prototype.slice.call(section.querySelectorAll('[data-nr-ptabs-tab]'));
    var panels = Array.prototype.slice.call(section.querySelectorAll('[data-nr-ptabs-panel]'));
    var carousels = panels.map(function (p) { return new Carousel(p); });

    function activate(index) {
      tabs.forEach(function (tab, i) {
        var on = i === index;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
      });
      panels.forEach(function (panel, i) {
        panel.hidden = i !== index;
      });
      // The panel was display:none, so its measurements are stale
      requestAnimationFrame(function () {
        if (carousels[index]) carousels[index].update();
      });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { activate(i); });
      tab.addEventListener('keydown', function (e) {
        var n = i;
        if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') n = 0;
        else if (e.key === 'End') n = tabs.length - 1;
        else return;
        e.preventDefault();
        activate(n);
        tabs[n].focus();
      });
    });

    section.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-nr-ptabs-add]');
      if (btn && section.contains(btn)) {
        e.preventDefault();
        addToCart(btn);
      }
    });

    if (tabs.length) activate(0);
  }

  function boot(scope) {
    (scope || document).querySelectorAll(ROOT).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    var section = e.target.closest && e.target.closest(ROOT);
    var panel = e.target.closest && e.target.closest('[data-nr-ptabs-panel]');
    if (!section || !panel) return;
    var tabs = section.querySelectorAll('[data-nr-ptabs-tab]');
    var panels = Array.prototype.slice.call(section.querySelectorAll('[data-nr-ptabs-panel]'));
    var idx = panels.indexOf(panel);
    if (idx >= 0 && tabs[idx]) tabs[idx].click();
  });
})();